import { getPermissionData, getUserAccountList, getUserInfo } from '@/api';

import { concurrency } from '~/utils/concurrency';
import { getSeoData } from '~/api/module/seo';
import type { ServerSeoData } from '~/api/interface';
import { useSeoData } from '~/hooks/useSeo';
import { useLicense } from '~/composables/license';
import { getTags } from '~/api/module/tags';
import { PageKey } from '~/constants/page';
import { getApplicationSceneTags } from '~/api/module/scenarios';

function onSuccess(data: { error?: Ref; data?: Ref } | undefined, cb: () => void) {
  if (!data) {
    return;
  }

  if (data.error?.value) {
    console.error(data.error.value);
    return;
  }

  if (data.data?.value) {
    cb();
  }
}

export default defineNuxtPlugin(async () => {
  const route = useRoute();

  if (route.name === PageKey.THIRD_AUTH_LOGIN) {
    return;
  }

  const userInfo = useUserInfo();
  const session = useSession();
  const accountList = useAccountList();
  const seoData = useSeoData();
  const permissionData = usePermissionData();
  const runtimeConfig = useRuntimeConfig();
  const request = useRequestURL();

  const bootRequestArray: Promise<any>[] = [
    session?.value?.user_id ? useAsyncData(getUserInfo) : Promise.resolve(),
    session?.value?.user_id ? useAsyncData(getUserAccountList) : Promise.resolve(),
    useAsyncData(getPermissionData),
    !runtimeConfig.public.privatization ? getSeoData() : Promise.resolve([]),
    useAsyncData(() => getTags({ group: 'template' })),
    useAsyncData(() => getTags({ group: 'character' })),
    useAsyncData('ScenariosTags', () => getApplicationSceneTags()),
    useAsyncData(
      'license',
      () =>
        fetch(
          isChinese()
            ? '//official-site.youyan.xyz/config/license.json'
            : '//fe.rendora.ai/config/third-license.json'
        ).then(res => res.json()),
      {
        server: true,
      }
    ),
  ];

  // 并行请求
  const result = await concurrency(bootRequestArray);

  const license = useLicense();

  const [
    userInfoResult,
    accountListResult,
    permissionDataResult,
    seoResult,
    templateTagResult,
    characterTagResult,
    scenariosTagResult,
    licenseResult,
  ] = result;

  license.value = licenseResult.data.value;

  onSuccess(userInfoResult, () => {
    userInfo.value = userInfoResult!.data.value?.data;
    CustomSettingApp.initAvatar(
      userInfoResult!.data.value?.data?.logo_url || '',
      userInfoResult!.data.value?.data?.default_logo_url || ''
    );

    CustomSettingApp.initLogo(
      userInfoResult!.data.value?.data?.platform_logo_url || '',
      userInfoResult!.data.value?.data?.default_platform_logo_url || ''
    );
  });

  onSuccess(accountListResult, () => {
    accountList.value = accountListResult!.data.value?.data || [];
  });

  onSuccess(permissionDataResult, () => {
    permissionData.value = permissionDataResult.data.value?.data;
  });

  if (import.meta.client) {
    const { data } = await getPermissionData();
    permissionData.value = data;
  }

  if (import.meta.server) {
    seoData.value = (seoResult || [])
      .filter((item: ServerSeoData) => item.path === request.pathname)
      .map((item: ServerSeoData) => ({
        path: `${item.path}`,
        title: item.seo_title,
        description: item.seo_description,
        keywords: item.seo_keywords,
      }));
  } else if (import.meta.client) {
    seoData.value = (seoResult || []).map((item: ServerSeoData) => ({
      path: `${item.path}`,
      title: item.seo_title,
      description: item.seo_description,
      keywords: item.seo_keywords,
    }));
  }

  const map = useTagMap();
  const centerTags = useCenterTags();
  const digitalTags = useCharacterTags();

  onSuccess(templateTagResult, () => {
    setTags(templateTagResult.data.value?.data?.tags, undefined, map);
    if (templateTagResult.data.value?.data) {
      centerTags.value = templateTagResult.data.value?.data;
    }
  });

  onSuccess(characterTagResult, () => {
    if (characterTagResult.data.value?.data) {
      digitalTags.value = characterTagResult.data.value?.data;
    }
  });

  const scenariosTags = useScenariosTags();

  onSuccess(scenariosTagResult, () => {
    if (scenariosTagResult?.data.value?.data) {
      scenariosTags.value = scenariosTagResult.data.value.data;
    }
  });
});
